import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "../lib/api";
import { damga } from "../lib/stamp";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, X, Loader2, Download, Filter, Columns3, Printer, Funnel, Upload, FileDown, AlertTriangle, GripVertical } from "lucide-react";
import ProductAutocomplete from "./ProductAutocomplete";
import StockAutocomplete from "./StockAutocomplete";
import KalipAutocomplete from "./KalipAutocomplete";
import { useAuth } from "../context/AuthContext";

const VARDIYA = ["00:00 - 08:00", "08:00 - 16:00", "16:00 - 24:00"];

const emptyFromFields = (fields) => {
  const o = {};
  fields.forEach((f) => { o[f.key] = f.default ?? ""; });
  return o;
};

const trDate = (v) => {
  if (typeof v !== "string") return v;
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
};

const valText = (c, row) => {
  const v = c.value ? c.value(row) : row[c.key];
  if (v === undefined || v === null) return "";
  return trDate(String(v));
};

// Türkçe karakter + büyük/küçük harf duyarsız normalizasyon (arama için)
const TR_MAP = { "İ": "i", "I": "i", "ı": "i", "Ş": "s", "ş": "s", "Ğ": "g", "ğ": "g", "Ü": "u", "ü": "u", "Ö": "o", "ö": "o", "Ç": "c", "ç": "c" };
const norm = (s) => String(s ?? "").replace(/[İIıŞşĞğÜüÖöÇç]/g, (m) => TR_MAP[m]).toLowerCase();

// Aramada eşleşen kısmı sarı ile vurgular (Türkçe/harf duyarsız)
const Highlight = ({ text, nq }) => {
  const s = String(text ?? "");
  if (!nq) return s || "—";
  const idx = norm(s).indexOf(nq);
  if (idx < 0) return s || "—";
  return (<>{s.slice(0, idx)}<mark className="bg-yellow-200 text-inherit rounded-[2px] px-0.5">{s.slice(idx, idx + nq.length)}</mark>{s.slice(idx + nq.length)}</>);
};

const EXPORT_ADLARI = {
  orders: "Gelen Siparişler",
  shaping: "Şekillenen Ürün",
  biscuit: "Bisküvi Giriş",
  finished: "Yapılan Ürün",
  kiln: "Bisküvi Fırını Yükleme",
  quality: "Kalite Takibi",
  stock: "Bisküvi Stok",
  devir: "Devir Bisküvi",
  planlama: "Üretim Planlama",
  siparis_harici: "Sipariş Harici",
  products: "Malzemeler",
  customers: "Müşteriler",
};
const trDosyaAdi = (kind) => EXPORT_ADLARI[kind] || kind;

export default function CrudTable({
  endpoint, listEndpoint, columns, fields = [], searchable = true, titleSingular = "Kayıt",
  dateField, vardiyaFilter = false, exportKind, readOnly = false, printTitle, negativeCols = [], adminOnlyEdit = false,
  warnNegativeKey, warnMessage, exportName, warnEmptyKeys, warnEmptyMessage, disableDelete = false, selectable = false, onMutate,
  bulkEditFields = [],
  quickFilters = null,
  bulkActions = null,
  toggleFilter = null,
}) {
  const { user } = useAuth();
  const canMutate = user?.role !== "viewer" && (!adminOnlyEdit || user?.role === "admin");
  const canEdit = !readOnly && canMutate;
  const canDelete = canEdit && !disableDelete;
  const showSel = canDelete || selectable;
  const getUrl = listEndpoint || endpoint;
  // Ürün/kalıp kodu yazınca otomatik dolan (readOnly veya fills hedefi) sütunlar — Excel çıktısı/şablonundan hariç tutulur
  const autoExcludeKeys = React.useMemo(() => {
    const s = new Set();
    fields.forEach((f) => {
      if (f.readOnly) s.add(f.key);
      if (f.fills) Object.values(f.fills).forEach((t) => s.add(t));
    });
    return s;
  }, [fields]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fStart, setFStart] = useState("");
  const [fEnd, setFEnd] = useState("");
  const [fVardiya, setFVardiya] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const prefsKey = `tblprefs:${listEndpoint || endpoint}`;
  const _prefs = (() => { try { return JSON.parse(localStorage.getItem(prefsKey) || "{}"); } catch { return {}; } })();
  const [hidden, setHidden] = useState(() => new Set(_prefs.hidden || []));
  const [widths, setWidths] = useState(() => _prefs.widths || {});
  const [order, setOrder] = useState(() => _prefs.order || []);
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [serverDefault, setServerDefault] = useState(null);
  const hadLocalPrefs = useRef(localStorage.getItem(prefsKey) != null);
  const [colMenu, setColMenu] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({});
  const [bulkEnabled, setBulkEnabled] = useState(() => new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [toggleOn, setToggleOn] = useState(false);
  const [colFilters, setColFilters] = useState(() => {
    const o = {}; Object.entries(_prefs.colFilters || {}).forEach(([k, v]) => { o[k] = new Set(v); }); return o;
  });
  const [openFilter, setOpenFilter] = useState(null);  // key with open popover
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [filterSearch, setFilterSearch] = useState("");
  const colMenuRef = useRef(null);
  const filterRef = useRef(null);
  const importRef = useRef(null);

  useEffect(() => {
    const cf = {}; Object.entries(colFilters).forEach(([k, v]) => { cf[k] = [...v]; });
    localStorage.setItem(prefsKey, JSON.stringify({ hidden: [...hidden], widths, colFilters: cf, order }));
  }, [hidden, widths, colFilters, order, prefsKey]);

  useEffect(() => {
    let ok = true;
    api.get("/table-defaults", { params: { key: listEndpoint || endpoint } }).then((r) => {
      if (!ok) return;
      const d = r.data || {};
      setServerDefault(d);
      if (!hadLocalPrefs.current) {
        if (Array.isArray(d.order) && d.order.length) setOrder(d.order);
        if (Array.isArray(d.hidden) && d.hidden.length) setHidden(new Set(d.hidden));
        if (d.widths && typeof d.widths === "object" && Object.keys(d.widths).length) setWidths(d.widths);
      }
    }).catch(() => {});
    return () => { ok = false; };
  }, [listEndpoint, endpoint]);

  useEffect(() => {
    const h = (e) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setColMenu(false);
      if (filterRef.current && !filterRef.current.contains(e.target)) setOpenFilter(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const buildParams = useCallback(() => {
    const p = {};
    if (dateField && fStart) p.start = fStart;
    if (dateField && fEnd) p.end = fEnd;
    if (dateField) p.date_field = dateField;
    if (vardiyaFilter && fVardiya) p.vardiya = fVardiya;
    return p;
  }, [fStart, fEnd, fVardiya, dateField, vardiyaFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(getUrl, { params: buildParams() });
      setRows(r.data);
      setSelected(new Set());
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [getUrl, buildParams]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => { setPage(0); }, [q, colFilters, toggleOn]);

  const clientExport = (fileName) => {
    const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const cols = orderedColumns.filter((c) => !hidden.has(c.key) && !autoExcludeKeys.has(c.key));
    const head = cols.map((c) => `<th style="background:#18181B;color:#fff;font-weight:bold;">${esc(c.label)}</th>`).join("");
    const body = displayRows.map((r) =>
      `<tr>${cols.map((c) => `<td>${esc(valText(c, r))}</td>`).join("")}</tr>`).join("");
    // Toplam satırı (yalnızca en sonda)
    const hasNum = cols.some((c) => c.num);
    let fl = false;
    const totRow = hasNum ? `<tr style="background:#E9E9E9;font-weight:bold;">${cols.map((c) => {
      if (c.num) return `<td>${fmtNum(numTotals[c.key])}</td>`;
      if (!fl) { fl = true; return `<td>TOPLAM</td>`; }
      return `<td></td>`;
    }).join("")}</tr>` : "";
    let fn = false;
    const negR = (negativeCols.length && Object.keys(negTotals).length) ? `<tr style="background:#FDE2E2;color:#b91c1c;font-weight:bold;">${cols.map((c) => {
      if (negTotals[c.key] !== undefined) return `<td>${fmtNum(negTotals[c.key])}</td>`;
      if (!fn) { fn = true; return `<td>ŞEKİLLENECEK ADET</td>`; }
      return `<td></td>`;
    }).join("")}</tr>` : "";
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><tr>${head}</tr>${body}${totRow}${negR}</table></body></html>`;
    const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${fileName} - ${damga()}.xls`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Excel indiriliyor");
  };

  // Excel'e Aktar: ekranda görünen veriyi (hesaplanan sütunlar dahil) dışa aktarır
  const doExport = () => clientExport(trDosyaAdi(exportKind));

  const downloadTemplate = async () => {
    const src = (fields.length ? fields : columns);
    const keys = src.map((f) => f.key).filter((k) => k && k !== "id" && !autoExcludeKeys.has(k));
    try {
      const res = await api.get(`/template/${exportKind}`, { params: { cols: keys.join(",") }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${trDosyaAdi(exportKind)} - Şablon - ${damga()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Şablon indirildi — ilk satırdaki başlıkları değiştirmeyin");
    } catch { toast.error("Şablon indirilemedi"); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post(`/import/${exportKind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${res.data.inserted} kayıt içe aktarıldı`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "İçe aktarma başarısız"); }
    finally { e.target.value = ""; }
  };

  const openNew = () => { setEditing(null); setForm(emptyFromFields(fields)); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickProduct = (p, field) => {
    setForm((f) => {
      const next = { ...f };
      Object.entries(field.fills || {}).forEach(([fromKey, toKey]) => { next[toKey] = p[fromKey] ?? ""; });
      next[field.key] = p.urun_kodu;
      return next;
    });
  };

  const save = async () => {
    for (const f of fields) {
      if (f.required && !String(form[f.key] ?? "").trim()) { toast.error(`${f.label} zorunludur`); return; }
    }
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      let v = form[f.key];
      if (f.type === "number") v = v === "" || v == null ? null : Number(v);
      payload[f.key] = v ?? "";
    });
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      setOpen(false);
      load();
      onMutate && onMutate();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm("Bu kayıt geri dönüşüme taşınsın mı?")) return;
    try { await api.delete(`${endpoint}/${row.id}`); toast.success("Geri dönüşüme taşındı"); load(); onMutate && onMutate(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} kayıt geri dönüşüme taşınsın mı?`)) return;
    try {
      await api.post(`${endpoint}/bulk-delete`, { ids });
      toast.success(`${ids.length} kayıt geri dönüşüme taşındı`);
      load();
      onMutate && onMutate();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const bulkFields = bulkEditFields.length
    ? bulkEditFields.map((k) => fields.find((f) => f.key === k)).filter(Boolean)
    : String(endpoint || "").startsWith("/entity/")
      ? fields.filter((f) => !f.readOnly && !["product", "kalip", "kalipkodu", "file"].includes(f.type))
      : [];
  const openBulkEdit = () => {
    const o = {};
    bulkFields.forEach((f) => { o[f.key] = f.default ?? ""; });
    setBulkForm(o); setBulkEnabled(new Set()); setBulkOpen(true);
  };
  const toggleBulkField = (k) => setBulkEnabled((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const applyBulk = async () => {
    const keys = [...bulkEnabled];
    if (!keys.length) { toast.error("Güncellenecek en az bir alan seçin"); return; }
    const updates = {};
    keys.forEach((k) => {
      const f = bulkFields.find((x) => x.key === k);
      let v = bulkForm[k];
      if (f?.type === "number") v = v === "" || v == null ? null : Number(v);
      updates[k] = v ?? "";
    });
    setBulkSaving(true);
    try {
      await api.post(`${endpoint}/bulk-update`, { ids: [...selected], updates });
      toast.success(`${selected.size} kayıt güncellendi`);
      setBulkOpen(false);
      load();
      onMutate && onMutate();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBulkSaving(false); }
  };

  const clearFilters = () => { setFStart(""); setFEnd(""); setFVardiya(""); setQ(""); };  const hasFilters = dateField || vardiyaFilter;

  const toggleCol = (key) => setHidden((h) => {
    const n = new Set(h); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  const startResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    const th = e.currentTarget.parentElement;
    const startX = e.clientX;
    const startW = widths[key] || th.offsetWidth;
    const onMove = (ev) => setWidths((w) => ({ ...w, [key]: Math.max(60, startW + ev.clientX - startX) }));
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const orderedColumns = React.useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const seen = new Set();
    const out = [];
    (order || []).forEach((k) => { if (byKey.has(k) && !seen.has(k)) { out.push(byKey.get(k)); seen.add(k); } });
    columns.forEach((c) => { if (!seen.has(c.key)) { out.push(c); seen.add(c.key); } });
    return out;
  }, [columns, order]);

  const moveColumn = (from, to) => {
    if (!from || from === to) return;
    const keys = orderedColumns.map((c) => c.key);
    const fi = keys.indexOf(from), ti = keys.indexOf(to);
    if (fi < 0 || ti < 0) return;
    keys.splice(ti, 0, keys.splice(fi, 1)[0]);
    setOrder(keys);
  };
  const resetOrder = () => setOrder([]);

  const saveAsDefault = async () => {
    try {
      const payload = { key: listEndpoint || endpoint, order: orderedColumns.map((c) => c.key), hidden: [...hidden], widths };
      await api.put("/table-defaults", payload);
      setServerDefault({ order: payload.order, hidden: payload.hidden, widths: payload.widths });
      toast.success("Sütun düzeni tüm kullanıcılar için varsayılan yapıldı.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi."); }
  };
  const applyDefault = () => {
    const d = serverDefault || {};
    setOrder(Array.isArray(d.order) ? d.order : []);
    setHidden(new Set(Array.isArray(d.hidden) ? d.hidden : []));
    if (d.widths && typeof d.widths === "object") setWidths(d.widths);
    toast.success("Varsayılan düzen uygulandı.");
  };

  const visibleColumns = orderedColumns.filter((c) => !hidden.has(c.key));
  const colByKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const toggleCount = toggleFilter ? rows.filter(toggleFilter.test).length : 0;

  // apply column header filters + global search client-side
  const sep = (s) => s.replace(/[.\-/]/g, ".");
  const gq = sep(norm(q).trim());
  let displayRows = rows.filter((r) =>
    (!toggleOn || toggleFilter.test(r)) &&
    Object.entries(colFilters).every(([k, set]) => set.has(valText(colByKey[k], r))) &&
    (!gq || columns.some((c) => sep(norm(valText(c, r))).includes(gq)))
  );
  if (sortKey && colByKey[sortKey]) {
    const c = colByKey[sortKey];
    const toN = (s) => parseFloat(String(s).replace(/\./g, "").replace(",", "."));
    displayRows = [...displayRows].sort((a, b) => {
      const va = valText(c, a), vb = valText(c, b);
      const na = toN(va), nb = toN(vb);
      let cmp;
      if (c.num && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
      else cmp = String(va).localeCompare(String(vb), "tr");
      return sortDir === "asc" ? cmp : -cmp;
    });
  }
  const totalPages = Math.max(1, Math.ceil(displayRows.length / pageSize));
  const curPage = Math.min(page, totalPages - 1);
  const pagedRows = displayRows.slice(curPage * pageSize, curPage * pageSize + pageSize);

  const fmtNum = (n) => Number(n || 0).toLocaleString("tr-TR");
  const negSet = new Set(negativeCols);
  const numTotals = {};
  visibleColumns.forEach((c) => {
    if (!c.num) return;
    // negativeCols sütunlarında TOPLAM sadece pozitif (+) değerleri toplar; eksiler "Şekillenecek Adet" satırında
    numTotals[c.key] = displayRows.reduce((sum, r) => {
      const v = Number(valText(c, r)) || 0;
      return sum + (negSet.has(c.key) ? (v > 0 ? v : 0) : v);
    }, 0);
  });
  const negTotals = {};
  negativeCols.forEach((k) => {
    const c = colByKey[k];
    if (c) negTotals[k] = displayRows.reduce((sum, r) => { const v = Number(valText(c, r)) || 0; return sum + (v < 0 ? v : 0); }, 0);
  });

  const distinctValues = (key) => {
    const c = colByKey[key];
    return Array.from(new Set(rows.map((r) => valText(c, r)))).sort((a, b) => a.localeCompare(b, "tr"));
  };

  const toggleFilterValue = (key, v, all) => setColFilters((prev) => {
    const cur = prev[key] ? new Set(prev[key]) : new Set(all);
    cur.has(v) ? cur.delete(v) : cur.add(v);
    const next = { ...prev };
    if (cur.size === all.length) delete next[key];
    else next[key] = cur;
    return next;
  });

  const setFilterAll = (key, all, on) => setColFilters((prev) => {
    const next = { ...prev };
    if (on) delete next[key];
    else next[key] = new Set();
    return next;
  });

  // Tek değer seç -> sadece o değere ait kayıtları göster
  const selectOnly = (key, v) => {
    setColFilters((prev) => ({ ...prev, [key]: new Set([v]) }));
    setOpenFilter(null);
    setPage(0);
  };
  const clearFilter = (key) => {
    setColFilters((prev) => { const n = { ...prev }; delete n[key]; return n; });
    setOpenFilter(null);
    setPage(0);
  };

  const printTable = () => {
    const title = printTitle || titleSingular;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Yazdırma penceresi açılamadı (pop-up engelli)"); return; }
    const head = visibleColumns.map((c) => `<th class="${c.num ? "r" : ""}">${c.label}</th>`).join("");
    const body = displayRows.map((r) =>
      `<tr>${visibleColumns.map((c) => `<td class="${c.num ? "r" : ""}">${valText(c, r) || ""}</td>`).join("")}</tr>`
    ).join("");
    const hasNum = visibleColumns.some((c) => c.num);
    let firstLabelDone = false;
    const totalRow = hasNum ? `<tr class="tot">${visibleColumns.map((c) => {
      if (c.num) return `<td class="r">${fmtNum(numTotals[c.key])}</td>`;
      if (!firstLabelDone) { firstLabelDone = true; return `<td>TOPLAM</td>`; }
      return `<td></td>`;
    }).join("")}</tr>` : "";
    let firstNegDone = false;
    const negRow = (negativeCols.length && Object.keys(negTotals).length) ? `<tr class="neg">${visibleColumns.map((c) => {
      if (negTotals[c.key] !== undefined) return `<td class="r">${fmtNum(negTotals[c.key])}</td>`;
      if (!firstNegDone) { firstNegDone = true; return `<td>ŞEKİLLENECEK ADET</td>`; }
      return c.num ? `<td class="r"></td>` : `<td></td>`;
    }).join("")}</tr>` : "";
    w.document.write(`<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><title>${title} - ${damga()}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, sans-serif; color: #111; }
        .hdr { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #18181B; padding-bottom:8px; margin-bottom:10px; }
        .hdr .brand { display:flex; align-items:center; gap:12px; }
        .hdr img { height: 42px; width:auto; object-fit:contain; }
        h1 { font-size: 16px; margin: 0; }
        .meta { font-size: 10px; color:#555; text-align:right; }
        table { border-collapse: collapse; width: 100%; font-size: 10px; }
        th, td { border: 1px solid #333; padding: 4px 6px; text-align: left; }
        th { background: #E9E9E9; font-weight: bold; }
        tr:nth-child(even) td { background: #F7F7F7; }
        .r { text-align: right; }
        .tot td { background: #E9E9E9; font-weight: bold; border-top: 2px solid #18181B; }
        .neg td { background: #FDE2E2; color: #b91c1c; font-weight: bold; }
      </style></head><body>
      <div class="hdr"><div class="brand"><img src="${window.location.origin}/by-bone-logo.png" alt="BY BONE" /><h1>${title}</h1></div><div class="meta">BY BONE PORSELEN — Üretim Takip Sistemi<br/>${new Date().toLocaleString("tr-TR")} — ${displayRows.length} kayıt</div></div>
      <table><thead><tr>${head}</tr></thead><tbody>${body}${totalRow}${negRow}</tbody></table>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  const allChecked = displayRows.length > 0 && displayRows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((s) => {
      const n = new Set(s);
      if (allChecked) displayRows.forEach((r) => n.delete(r.id));
      else displayRows.forEach((r) => n.add(r.id));
      return n;
    });
  };
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="p-6">
      {warnNegativeKey && (() => {
        const negs = rows.filter((r) => (Number(r[warnNegativeKey]) || 0) < 0);
        if (negs.length === 0) return null;
        return (
          <div className="mb-3 flex items-start gap-2 border-l-4 border-red-500 bg-red-50 text-red-700 rounded-sm px-4 py-3" data-testid="negative-warning">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-bold">Uyarı: {negs.length} ürün eksiye düşüyor!</span>{" "}
              <span className="font-semibold">{warnMessage}</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {negs.slice(0, 20).map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-white border border-red-200 rounded-sm px-1.5 py-0.5 text-xs font-semibold mono">
                    {r.kalip_kodu || r.urun_kodu} <span className="text-red-500">({(Number(r[warnNegativeKey]) || 0).toLocaleString("tr-TR")})</span>
                  </span>
                ))}
                {negs.length > 20 && <span className="text-xs text-red-500">+{negs.length - 20} daha</span>}
              </div>
            </div>
          </div>
        );
      })()}
      {warnEmptyKeys && warnEmptyKeys.length > 0 && (() => {
        const isEmpty = (v) => v === null || v === undefined || String(v).trim() === "";
        const bad = rows.filter((r) => warnEmptyKeys.some((k) => isEmpty(r[k])));
        if (bad.length === 0) return null;
        return (
          <div className="mb-3 flex items-start gap-2 border-l-4 border-red-500 bg-red-50 text-red-700 rounded-sm px-4 py-3" data-testid="empty-warning">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-bold">Uyarı: Eksik veri var!</span>{" "}
              <span className="font-semibold">{warnEmptyMessage || "Bazı kayıtlarda otomatik gelmesi gereken alanlar boş (ürün/kalıp Malzemeler'de tanımlı değil)."}</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {bad.slice(0, 20).map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-white border border-red-200 rounded-sm px-1.5 py-0.5 text-xs font-semibold mono">
                    {r.urun_kodu || r.kalip_kodu || "?"}
                  </span>
                ))}
                {bad.length > 20 && <span className="text-xs text-red-500">+{bad.length - 20} daha</span>}
              </div>
            </div>
          </div>
        );
      })()}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        {searchable ? (
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={15} className="absolute left-3 top-2.5 text-zinc-400" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Ara (tüm sütunlarda)"
              className="w-full border border-zinc-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
          </div>
        ) : <div />}
        <div className="flex items-center gap-2">
          <div className="relative" ref={colMenuRef}>
            <button onClick={() => setColMenu((v) => !v)} data-testid="column-menu-btn"
              className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm transition-colors">
              <Columns3 size={15} /> Sütunlar
            </button>
            {colMenu && (
              <div className="absolute right-0 mt-1 z-50 w-64 max-h-80 overflow-y-auto bg-white border border-zinc-300 shadow-lg rounded-sm py-1" data-testid="column-menu">
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-100">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">Sütun Sırası / Göster-Gizle</span>
                  <button onClick={resetOrder} data-testid="col-order-reset" className="text-[10px] text-amber-600 hover:underline font-semibold">Sırayı Sıfırla</button>
                </div>
                <div className="px-3 py-1 text-[10px] text-zinc-400">Sırayı değiştirmek için ↑ ↓ kullanın veya başlığı sürükleyin.</div>
                {orderedColumns.map((c, idx) => (
                  <div key={c.key} className="flex items-center gap-1.5 px-2 py-1.5 text-sm hover:bg-zinc-50" data-testid={`col-row-${c.key}`}>
                    <div className="flex flex-col -my-1">
                      <button disabled={idx === 0} onClick={() => moveColumn(c.key, orderedColumns[idx - 1].key)} data-testid={`col-up-${c.key}`}
                        className="leading-none text-zinc-400 hover:text-amber-600 disabled:opacity-25 text-[11px]">▲</button>
                      <button disabled={idx === orderedColumns.length - 1} onClick={() => moveColumn(c.key, orderedColumns[idx + 1].key)} data-testid={`col-down-${c.key}`}
                        className="leading-none text-zinc-400 hover:text-amber-600 disabled:opacity-25 text-[11px]">▼</button>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0" data-testid={`col-toggle-${c.key}`}>
                      <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => toggleCol(c.key)} className="accent-amber-600" />
                      <span className="truncate">{c.label}</span>
                    </label>
                  </div>
                ))}
                <div className="border-t border-zinc-100 px-2 py-2 flex flex-col gap-1.5">
                  {user?.role === "admin" && (
                    <button onClick={saveAsDefault} data-testid="col-save-default"
                      className="w-full text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white px-2.5 py-1.5 rounded-sm transition-colors">
                      Herkes için varsayılan yap
                    </button>
                  )}
                  {serverDefault && Array.isArray(serverDefault.order) && serverDefault.order.length > 0 && (
                    <button onClick={applyDefault} data-testid="col-apply-default"
                      className="w-full text-xs font-semibold border border-zinc-300 hover:bg-zinc-50 text-zinc-700 px-2.5 py-1.5 rounded-sm transition-colors">
                      Varsayılan düzene dön
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <button onClick={printTable} data-testid="pdf-btn"
            className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm transition-colors">
            <Printer size={15} /> PDF
          </button>
          {exportKind && (
            <>
              <input ref={importRef} type="file" accept=".xlsx,.xls" onChange={doImport} className="hidden" data-testid="import-input" />
              <button onClick={downloadTemplate} data-testid="template-btn"
                className="flex items-center gap-2 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-semibold px-3 py-2 rounded-sm transition-colors">
                <FileDown size={15} /> Şablon İndir
              </button>
              <button onClick={() => importRef.current?.click()} data-testid="import-btn"
                className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm transition-colors">
                <Upload size={15} /> Excel'den Al
              </button>
              <button onClick={doExport} data-testid="export-btn"
                className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm transition-colors">
                <Download size={15} /> Excel'e Aktar
              </button>
            </>
          )}
          {!exportKind && exportName && (
            <button onClick={() => clientExport(exportName)} data-testid="export-btn"
              className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm transition-colors">
              <Download size={15} /> Excel'e Aktar
            </button>
          )}
          {canEdit && (
            <button onClick={openNew} data-testid="add-new-btn"
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors">
              <Plus size={16} /> Yeni Ekle
            </button>
          )}
        </div>
      </div>

      {hasFilters && (
        <div className="flex items-end gap-3 mb-4 flex-wrap bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2.5" data-testid="filter-bar">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide pb-2"><Filter size={13} /> Filtre</div>
          {dateField && (
            <>
              <div><label className="block text-[10px] font-semibold text-zinc-500 uppercase mb-0.5">Başlangıç</label>
                <input type="date" data-testid="filter-start" value={fStart} onChange={(e) => setFStart(e.target.value)}
                  className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono focus:outline-none focus:ring-2 focus:ring-amber-600" /></div>
              <div><label className="block text-[10px] font-semibold text-zinc-500 uppercase mb-0.5">Bitiş</label>
                <input type="date" data-testid="filter-end" value={fEnd} onChange={(e) => setFEnd(e.target.value)}
                  className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono focus:outline-none focus:ring-2 focus:ring-amber-600" /></div>
            </>
          )}
          {vardiyaFilter && (
            <div><label className="block text-[10px] font-semibold text-zinc-500 uppercase mb-0.5">Vardiya</label>
              <select data-testid="filter-vardiya" value={fVardiya} onChange={(e) => setFVardiya(e.target.value)}
                className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-600">
                <option value="">Tümü</option>
                {VARDIYA.map((v) => <option key={v} value={v}>{v}</option>)}
              </select></div>
          )}
          <button onClick={clearFilters} data-testid="filter-clear" className="text-xs text-zinc-500 hover:text-red-600 pb-2 underline">Temizle</button>
        </div>
      )}

      {showSel && selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-amber-50 border border-amber-200 rounded-sm px-4 py-2" data-testid="bulk-bar">
          <span className="text-sm font-semibold text-amber-800">{selected.size} kayıt seçildi</span>
          {(() => {
            const selRows = displayRows.filter((r) => selected.has(r.id));
            const parts = visibleColumns.filter((c) => c.num).map((c) => {
              const sum = selRows.reduce((a, r) => a + (Number(valText(c, r)) || 0), 0);
              return `${c.label}: ${fmtNum(sum)}`;
            });
            return parts.length ? <span className="text-xs text-amber-700" data-testid="selected-totals">{parts.join(" · ")}</span> : null;
          })()}
          {canEdit && bulkFields.length > 0 && (
            <button onClick={openBulkEdit} data-testid="bulk-update-btn"
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-1.5 rounded-sm">
              <Pencil size={14} /> Toplu Güncelle
            </button>
          )}
          {canDelete && (
            <button onClick={bulkDelete} data-testid="bulk-delete-btn"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-3 py-1.5 rounded-sm">
              <Trash2 size={14} /> Seçilenleri Sil
            </button>
          )}
          {bulkActions && bulkActions(displayRows.filter((r) => selected.has(r.id)), () => setSelected(new Set()))}
          <button onClick={() => setSelected(new Set())} className="text-xs text-zinc-500 underline ml-auto" data-testid="clear-selection-btn">Seçimi kaldır</button>
        </div>
      )}

      {toggleFilter && (
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => setToggleOn((v) => !v)} data-testid="toggle-filter-btn"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${toggleOn ? "bg-red-600 text-white border-red-600" : "bg-white text-red-600 border-red-300 hover:bg-red-50"}`}>
            {toggleFilter.label} <span className={`rounded-full px-1.5 ${toggleOn ? "bg-white/25" : "bg-red-100"}`} data-testid="toggle-filter-count">{toggleCount}</span>
          </button>
          {toggleOn && <button onClick={() => setToggleOn(false)} className="text-xs text-zinc-500 underline" data-testid="toggle-filter-clear">Tümünü göster</button>}
        </div>
      )}

      {quickFilters && (() => {
        const key = quickFilters.key;
        const active = colFilters[key];
        const isOn = (v) => active && active.size === 1 && active.has(v);
        return (
          <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="quick-filters">
            {quickFilters.label && <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">{quickFilters.label}:</span>}
            <button onClick={() => clearFilter(key)} data-testid="quick-filter-all"
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${!active ? "bg-zinc-800 text-white border-zinc-800" : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"}`}>
              Tümü
            </button>
            {quickFilters.options.map((o) => (
              <button key={o.value} onClick={() => selectOnly(key, o.value)} data-testid={`quick-filter-${o.value}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${isOn(o.value) ? "bg-amber-600 text-white border-amber-600" : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"}`}>
                {o.label}
              </button>
            ))}
          </div>
        );
      })()}

      <div className="border border-zinc-200 bg-white rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 230px)" }}>
        <table className="w-full data-table" data-testid="data-table" style={{ tableLayout: "auto" }}>
          <thead className="sticky top-0 z-20 bg-zinc-100">
            <tr>
              {showSel && (
                <th className="w-8">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="select-all-checkbox" className="accent-amber-600" />
                </th>
              )}
              {visibleColumns.map((c) => {
                const active = !!colFilters[c.key];
                return (
                  <th key={c.key} title={c.label}
                    onDragOver={(e) => { if (dragKey) { e.preventDefault(); setDragOverKey(c.key); } }}
                    onDragLeave={() => setDragOverKey((k) => (k === c.key ? null : k))}
                    onDrop={(e) => { e.preventDefault(); moveColumn(dragKey, c.key); setDragKey(null); setDragOverKey(null); }}
                    className={`relative align-top ${c.num ? "text-right" : ""} ${dragOverKey === c.key && dragKey && dragKey !== c.key ? "bg-amber-100 outline outline-2 outline-amber-400" : ""} ${dragKey === c.key ? "opacity-40" : ""}`}
                    style={widths[c.key] ? { width: widths[c.key], minWidth: widths[c.key], maxWidth: widths[c.key] } : undefined}>
                    <span className="inline-flex items-start gap-1 whitespace-normal break-words leading-tight">
                      <span draggable onDragStart={(e) => { setDragKey(c.key); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", c.key); } catch (err) {} }}
                        onDragEnd={() => { setDragKey(null); setDragOverKey(null); }}
                        data-testid={`col-drag-${c.key}`} title="Sütunu taşımak için sürükleyin"
                        className="cursor-grab active:cursor-grabbing text-zinc-400 hover:text-amber-600 mt-0.5 shrink-0">
                        <GripVertical size={12} />
                      </span>
                      {c.label}
                      <button onClick={(e) => {
                          e.stopPropagation();
                          setFilterSearch("");
                          if (openFilter === c.key) { setOpenFilter(null); return; }
                          const rect = e.currentTarget.getBoundingClientRect();
                          setFilterPos({
                            top: Math.min(rect.bottom + 4, window.innerHeight - 320),
                            left: Math.max(8, Math.min(rect.left, window.innerWidth - 260)),
                          });
                          setOpenFilter(c.key);
                        }}
                        data-testid={`filter-icon-${c.key}`} className={`p-0.5 rounded hover:bg-zinc-200 ${active ? "text-amber-600" : "text-zinc-400"}`}>
                        <Funnel size={12} />
                      </button>
                    </span>
                    {openFilter === c.key && (
                      <div ref={filterRef} style={{ position: "fixed", top: filterPos.top, left: filterPos.left }}
                        className="z-[60] w-60 bg-white border border-zinc-300 shadow-xl rounded-sm text-left font-normal normal-case tracking-normal" data-testid={`header-filter-${c.key}`}>
                        <div className="border-b border-zinc-100">
                          <button onClick={() => { setSortKey(c.key); setSortDir("asc"); }} data-testid={`sort-asc-${c.key}`}
                            className={`w-full text-left px-2.5 py-1.5 text-xs font-semibold hover:bg-amber-50 flex items-center gap-2 ${sortKey === c.key && sortDir === "asc" ? "text-amber-700" : "text-zinc-700"}`}>
                            <span className="font-black text-amber-600">A→Z</span> Küçükten Büyüğe Sırala
                          </button>
                          <button onClick={() => { setSortKey(c.key); setSortDir("desc"); }} data-testid={`sort-desc-${c.key}`}
                            className={`w-full text-left px-2.5 py-1.5 text-xs font-semibold hover:bg-amber-50 flex items-center gap-2 border-t border-zinc-100 ${sortKey === c.key && sortDir === "desc" ? "text-amber-700" : "text-zinc-700"}`}>
                            <span className="font-black text-amber-600">Z→A</span> Büyükten Küçüğe Sırala
                          </button>
                        </div>
                        <div className="p-2 border-b border-zinc-100">
                          <input autoFocus value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} placeholder="Ara..."
                            data-testid={`filter-search-${c.key}`}
                            className="w-full border border-zinc-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-600" />
                        </div>
                        <div className="max-h-56 overflow-y-auto py-1">
                          {(() => {
                            const all = distinctValues(c.key);
                            const allChecked = !colFilters[c.key];
                            return (
                              <label className="flex items-center gap-2 px-2.5 py-1 text-xs font-semibold hover:bg-zinc-50 cursor-pointer border-b border-zinc-100">
                                <input type="checkbox" checked={allChecked} onChange={() => setFilterAll(c.key, all, !allChecked)} className="accent-amber-600" data-testid={`filter-selectall-${c.key}`} />
                                <span>(Tümünü Seç)</span>
                              </label>
                            );
                          })()}
                          {distinctValues(c.key).filter((v) => norm(v).includes(norm(filterSearch))).slice(0, 500).map((v) => {
                            const all = distinctValues(c.key);
                            const checked = !colFilters[c.key] || colFilters[c.key].has(v);
                            return (
                              <label key={v} data-testid={`filter-option-${c.key}`}
                                className="flex items-center gap-2 px-2.5 py-1 text-xs hover:bg-zinc-50 cursor-pointer">
                                <input type="checkbox" checked={checked} onChange={() => toggleFilterValue(c.key, v, all)} className="accent-amber-600" />
                                <span className="truncate">{v || "(boş)"}</span>
                              </label>
                            );
                          })}
                        </div>
                        <div className="flex justify-between items-center p-2 border-t border-zinc-100">
                          <button onClick={() => clearFilter(c.key)} data-testid={`filter-all-${c.key}`} className="text-xs text-zinc-500 hover:underline">Filtreyi Temizle</button>
                          <button onClick={() => setOpenFilter(null)} data-testid={`filter-ok-${c.key}`} className="text-xs bg-zinc-800 text-white px-3 py-1 rounded-sm hover:bg-zinc-700">Tamam</button>
                        </div>
                      </div>
                    )}
                    <span onMouseDown={(e) => startResize(e, c.key)} data-testid={`resize-${c.key}`}
                      className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-amber-400" />
                  </th>
                );
              })}
              {canEdit && <th className="text-right">İşlem</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleColumns.length + (showSel ? 1 : 0) + (canEdit ? 1 : 0)} className="text-center py-10 text-zinc-400">
                <Loader2 className="animate-spin inline" size={18} /> Yükleniyor...
              </td></tr>
            ) : displayRows.length === 0 ? (
              <tr><td colSpan={visibleColumns.length + (showSel ? 1 : 0) + (canEdit ? 1 : 0)} className="text-center py-10 text-zinc-400">Kayıt yok.</td></tr>
            ) : pagedRows.map((row) => (
              <tr key={row.id} data-testid={`row-${row.id}`} onDoubleClick={() => { if (canEdit) openEdit(row); }}
                className={`${selected.has(row.id) ? "bg-amber-50" : ""} ${canEdit ? "cursor-pointer" : ""}`}
                title={canEdit ? "Düzenlemek için çift tıklayın" : undefined}>
                {showSel && (
                  <td className="w-8">
                    <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleOne(row.id)} data-testid={`row-select-${row.id}`} className="accent-amber-600" />
                  </td>
                )}
                {visibleColumns.map((c) => (
                  <td key={c.key} className={c.num ? "num" : (c.mono ? "mono" : "")}
                    style={widths[c.key] ? { width: widths[c.key], minWidth: widths[c.key], maxWidth: widths[c.key], overflow: "hidden", textOverflow: "ellipsis" } : undefined}>
                    {c.render ? c.render(row) : <Highlight text={trDate(row[c.key])} nq={gq} />}
                  </td>
                ))}
                {canEdit && (
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(row)} data-testid={`edit-${row.id}`} className="p-1.5 text-zinc-500 hover:text-amber-600 hover:bg-amber-50 rounded-sm"><Pencil size={14} /></button>
                      {canDelete && <button onClick={() => del(row)} data-testid={`delete-${row.id}`} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={14} /></button>}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {displayRows.length > 0 && (
            <tfoot>
              <tr className="bg-zinc-100 font-bold border-t-2 border-zinc-300" data-testid="totals-row">
                {showSel && <td className="w-8"></td>}
                {visibleColumns.map((c, i) => (
                  <td key={c.key} className={c.num ? "num" : ""}>
                    {i === 0 ? "TOPLAM" : (c.num ? fmtNum(numTotals[c.key]) : "")}
                  </td>
                ))}
                {canEdit && <td></td>}
              </tr>
              {negativeCols.length > 0 && (
                <tr className="bg-red-50 font-bold text-red-700" data-testid="negative-totals-row">
                  {showSel && <td className="w-8"></td>}
                  {visibleColumns.map((c, i) => (
                    <td key={c.key} className={c.num ? "num" : ""}>
                      {i === 0 ? "Şekillenecek Adet" : (negTotals[c.key] !== undefined ? fmtNum(negTotals[c.key]) : "")}
                    </td>
                  ))}
                  {canEdit && <td></td>}
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-zinc-500">
          {displayRows.length} kayıt{displayRows.length !== rows.length ? ` (toplam ${rows.length}, filtreli)` : ""}
          {displayRows.length > 0 ? ` · Gösterilen ${curPage * pageSize + 1}-${Math.min(displayRows.length, (curPage + 1) * pageSize)}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Sayfa boyutu</span>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} data-testid="page-size"
            className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-amber-600">
            {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button onClick={() => setPage(0)} disabled={curPage === 0} data-testid="page-first" className="px-2 py-1 text-xs border border-zinc-300 rounded-sm hover:bg-zinc-50 disabled:opacity-40">«</button>
          <button onClick={() => setPage(curPage - 1)} disabled={curPage === 0} data-testid="page-prev" className="px-2 py-1 text-xs border border-zinc-300 rounded-sm hover:bg-zinc-50 disabled:opacity-40">‹ Önceki</button>
          <span className="text-xs font-semibold px-1" data-testid="page-info">Sayfa {curPage + 1} / {totalPages}</span>
          <button onClick={() => setPage(curPage + 1)} disabled={curPage >= totalPages - 1} data-testid="page-next" className="px-2 py-1 text-xs border border-zinc-300 rounded-sm hover:bg-zinc-50 disabled:opacity-40">Sonraki ›</button>
          <button onClick={() => setPage(totalPages - 1)} disabled={curPage >= totalPages - 1} data-testid="page-last" className="px-2 py-1 text-xs border border-zinc-300 rounded-sm hover:bg-zinc-50 disabled:opacity-40">»</button>
        </div>
      </div>

      {open && !readOnly && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onMouseDown={() => setOpen(false)}>
          <div className="bg-white rounded-sm shadow-xl w-full max-w-2xl my-8" onMouseDown={(e) => e.stopPropagation()} data-testid="crud-dialog">
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
              <h3 className="font-head font-bold text-lg">{editing ? `${titleSingular} Düzenle` : `Yeni ${titleSingular}`}</h3>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.key} className={f.span === 2 ? "col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-zinc-600 mb-1 uppercase tracking-wide">
                    {f.label}{f.required && <span className="text-red-500"> *</span>}
                  </label>
                  {f.type === "product" ? (
                    <ProductAutocomplete value={form[f.key]} testid={`field-${f.key}`}
                      onChange={(v) => setField(f.key, v)} onPick={(p) => onPickProduct(p, f)} />
                  ) : f.type === "kalip" ? (
                    <StockAutocomplete value={form[f.key]} testid={`field-${f.key}`}
                      onChange={(v) => setField(f.key, v)}
                      onPick={(row) => setForm((prev) => {
                        const next = { ...prev, [f.key]: row.kalip_kodu };
                        Object.entries(f.fills || {}).forEach(([from, to]) => { next[to] = row[from] ?? ""; });
                        return next;
                      })} />
                  ) : f.type === "kalipkodu" ? (
                    <KalipAutocomplete value={form[f.key]} testid={`field-${f.key}`}
                      onChange={(v) => setField(f.key, v)}
                      onPick={(p) => setForm((prev) => {
                        const next = { ...prev, [f.key]: p.kalip_kodu };
                        Object.entries(f.fills || {}).forEach(([from, to]) => { next[to] = p[from] ?? ""; });
                        return next;
                      })} />
                  ) : f.type === "select" ? (
                    <select data-testid={`field-${f.key}`} value={form[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)}
                      className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-600">
                      <option value="">Seçiniz</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "textarea" ? (
                    <textarea data-testid={`field-${f.key}`} value={form[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)} rows={2}
                      className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
                  ) : (
                    <input data-testid={`field-${f.key}`}
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "time" ? "time" : "text"}
                      value={form[f.key] ?? ""} readOnly={f.readOnly} onChange={(e) => setField(f.key, e.target.value)}
                      className={`w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-600 ${f.type === "number" || f.type === "time" || f.mono ? "mono" : ""} ${f.readOnly ? "bg-zinc-100 text-zinc-500" : ""}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 border-t border-zinc-200 px-5 py-3">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">İptal</button>
              <button onClick={save} disabled={saving} data-testid="save-btn" className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-sm flex items-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="animate-spin" size={15} />} Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onMouseDown={() => setBulkOpen(false)}>
          <div className="bg-white rounded-sm shadow-xl w-full max-w-xl my-8" onMouseDown={(e) => e.stopPropagation()} data-testid="bulk-edit-dialog">
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
              <h3 className="font-head font-bold text-lg">Toplu Güncelle · {selected.size} kayıt</h3>
              <button onClick={() => setBulkOpen(false)} className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
            </div>
            <div className="px-5 py-3 text-xs text-zinc-500 border-b border-zinc-100">
              Güncellemek istediğiniz alanların kutucuğunu işaretleyip değeri girin. Yalnızca işaretli alanlar tüm seçili satırlara uygulanır.
            </div>
            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {bulkFields.map((f) => {
                const on = bulkEnabled.has(f.key);
                return (
                  <div key={f.key} className={`flex items-center gap-3 border rounded-sm px-3 py-2 ${on ? "border-sky-300 bg-sky-50/50" : "border-zinc-200"}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleBulkField(f.key)} className="accent-sky-600" data-testid={`bulk-enable-${f.key}`} />
                    <label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide w-40 shrink-0">{f.label}</label>
                    {f.type === "select" ? (
                      <select disabled={!on} data-testid={`bulk-field-${f.key}`} value={bulkForm[f.key] ?? ""} onChange={(e) => setBulkForm((s) => ({ ...s, [f.key]: e.target.value }))}
                        className="flex-1 border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-600 disabled:bg-zinc-100 disabled:text-zinc-400">
                        <option value="">Seçiniz</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : f.type === "textarea" ? (
                      <textarea disabled={!on} data-testid={`bulk-field-${f.key}`} rows={2} value={bulkForm[f.key] ?? ""} onChange={(e) => setBulkForm((s) => ({ ...s, [f.key]: e.target.value }))}
                        className="flex-1 border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 disabled:bg-zinc-100 disabled:text-zinc-400" />
                    ) : (
                      <input disabled={!on} data-testid={`bulk-field-${f.key}`}
                        type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                        value={bulkForm[f.key] ?? ""} onChange={(e) => setBulkForm((s) => ({ ...s, [f.key]: e.target.value }))}
                        className={`flex-1 border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 disabled:bg-zinc-100 disabled:text-zinc-400 ${f.type === "number" || f.mono ? "mono" : ""}`} />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 border-t border-zinc-200 px-5 py-3">
              <button onClick={() => setBulkOpen(false)} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">İptal</button>
              <button onClick={applyBulk} disabled={bulkSaving || bulkEnabled.size === 0} data-testid="bulk-apply-btn"
                className="px-4 py-2 text-sm bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-sm flex items-center gap-2 disabled:opacity-60">
                {bulkSaving && <Loader2 className="animate-spin" size={15} />} {selected.size} Kaydı Güncelle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
